"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CameraController = void 0;
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const streem_error_1 = require("../../streem_error");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const logger_1 = require("../../util/logger");
const toolbox_1 = require("@streem/toolbox");
const filter_empty_array_1 = require("../../util/rx/filter_empty_array");
const first_from_group_1 = require("../../util/rx/first_from_group");
const utils_1 = require("../wall/utils");
const last_created_at_1 = require("../../util/rx/last_created_at");
const analytics_1 = require("@streem/analytics");
const ANALYTICS_CAMERA_SUBJECT_MAP = {
    [streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_PERSON]: 'PERSON',
    [streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN]: 'SCREEN',
    [streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD]: 'WORLD',
};
class CameraController {
    constructor(config) {
        this.config = config;
        this.log = logger_1.logger.extend('sdk:cam_controller');
        this.destroyed = new rxjs_1.Subject();
        (0, toolbox_1.invariant)(this.config.context.localUserId, 'Expected localUserId to be defined when initializing CameraController');
        (0, toolbox_1.invariant)(this.config.context.remoteUserId, 'Expected remoteUserId to be defined when initializing CameraController');
        this.requests = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('cameraSubjectRequest'), (0, utils_1.accumulateAndMapWallEvents)('cameraSubjectRequest'), this.requestedOf(this.config.context.localUserId), (0, filter_empty_array_1.filterEmptyArray)(), (0, last_created_at_1.lastCreatedAt)(), (0, operators_1.scan)((all, current) => [current, ...all], []));
        this.responses = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('cameraSubjectResponse'), (0, utils_1.accumulateAndMapWallEvents)('cameraSubjectResponse'), (0, filter_empty_array_1.filterEmptyArray)(), (0, last_created_at_1.lastCreatedAt)(), (0, operators_1.distinctUntilKeyChanged)('inResponseToWallItemId'), (0, operators_1.scan)((all, current) => [current, ...all], []), (0, operators_1.shareReplay)(1));
        // Removes the pipe operator filtering  by local user ID so the latest request is always returned from any user request
        this.latestRequest = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('cameraSubjectRequest'), (0, utils_1.accumulateAndMapWallEvents)('cameraSubjectRequest'), (0, filter_empty_array_1.filterEmptyArray)(), (0, last_created_at_1.lastCreatedAt)(), (0, operators_1.scan)((all, current) => [current, ...all], []), (0, first_from_group_1.firstFromGroup)());
        this.latestResponse = this.responses.pipe((0, filter_empty_array_1.filterEmptyArray)(), (0, first_from_group_1.firstFromGroup)());
        // When exiting screenshare, we want to return the user to the previous camera subject used before entering screenshare
        // This property filters the video track wall items to return the most recent camera subject used by the remote participant that is not screenshare
        this.latestRemoteCameraSubjectThatIsNotScreenshare = this.config.roomWall.itemsByType.pipe((0, utils_1.findInGroup)('videoTrack'), (0, utils_1.mapWallItemFromWallItemEvents)('videoTrack'), (0, operators_1.filter)(track => {
            var _a;
            return track !== undefined &&
                track.cameraSubject !== streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_SCREEN &&
                track.ownerUserId === ((_a = this.config.context.remoteUserId) === null || _a === void 0 ? void 0 : _a.toString());
        }), (0, operators_1.map)(track => (track === null || track === void 0 ? void 0 : track.cameraSubject) || streem_sdk_protobuf_1.CameraSubject.CAMERA_SUBJECT_WORLD), (0, operators_1.shareReplay)(1));
    }
    disconnect() {
        this.destroyed.next(undefined);
    }
    /**
     * Adds a camera flip (camera subject request) to the room wall
     */
    requestCameraSubjectChange(cameraSubject) {
        const { remoteUserId, remoteDeviceId } = this.config.context;
        if (!remoteUserId || !remoteDeviceId) {
            const msg = 'remoteUserId and remoteDeviceId are required for cameraSubject request';
            this.log.error(msg);
            throw new streem_error_1.StreemError(msg);
        }
        const requestId = this.config.roomWall.addCameraSubjectRequest(remoteUserId, remoteDeviceId, cameraSubject);
        (0, analytics_1.recordCameraSubjectChangeRequested)(this.config.context.roomId, ANALYTICS_CAMERA_SUBJECT_MAP[cameraSubject]);
        this.log.info(`Camera Flip requested by ${remoteUserId}`, requestId);
        return requestId;
    }
    fulfillCameraSubjectChangeRequest(inResponseToWallItemId, cameraSubject) {
        // Let the Expert know the request was accepted
        const requestId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, true, streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED);
        (0, analytics_1.recordCameraSubjectChanged)(this.config.context.roomId, ANALYTICS_CAMERA_SUBJECT_MAP[cameraSubject]);
        this.log.info(`Camera Flip fulfilled by ${this.config.context.localUserId} with CameraSubject ${cameraSubject}.`, requestId);
        return requestId;
    }
    rejectCameraSubjectChangeRequest(inResponseToWallItemId) {
        const responseId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, false, streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED);
        (0, analytics_1.recordCameraSubjectChangeRejected)(this.config.context.roomId);
        this.log.info(`Camera Flip rejected by ${this.config.context.localUserId}`);
        return responseId;
    }
    abortCameraSubjectChangeRequest(inResponseToWallItemId) {
        const responseId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, false, streem_sdk_protobuf_1.CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ABORTED);
        (0, analytics_1.recordCameraSubjectChangeAborted)(this.config.context.roomId);
        this.log.info(`Camera Flip aborted by ${this.config.context.localUserId}`);
        return responseId;
    }
    /**
     * Filters camera subject requests by who is requested to **provide** the camera subject
     * Elsewhere, we use `requestedOf` in the request object, but for CameraSubjectRequests
     * we use `requestedFor`. For that reason, we have our own private method here to filter.
     * @param userId the user who should provide the cameraSubject
     */
    requestedOf(userId) {
        return (0, operators_1.map)((requests) => requests.filter(request => request.requestedForUserId === userId.toString()));
    }
}
exports.CameraController = CameraController;
//# sourceMappingURL=camera_controller.js.map